import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ButtonBase from '@material-ui/core/ButtonBase';
import Grid from '@material-ui/core/Grid';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import YouTubeIcon from '@material-ui/icons/YouTube';
import ContactForm from './ContactForm';
import LazyAnimation from './LazyAnimation';

const useStyles = makeStyles(theme => ({
  wrapper: {
    paddingTop: 90,
    paddingBottom: 90,
    position: 'relative',
    backgroundColor: '#FFFFFF',
  },
  parallaxWrap: {
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  parallaxBg: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage:
      'url("https://solutions.crediblemind.com/wp-content/uploads/2019/12/AdobeStock_305498033.jpeg")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'top',
    backgroundAttachment: 'fixed',
  },
  container: {
    width: '80%',
    maxWidth: 1080,
    margin: 'auto',
  },
  leftColumn: {
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  rightColumn: {
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  header: {
    fontFamily: 'PoppinsSemiBold',
    fontWeight: '600',
    fontSize: 30,
    lineHeight: '1.5em',
    textAlign: 'left',
    paddingBottom: '5.82%',
    color: '#333333',
  },
  body: {
    textAlign: 'center',
    fontSize: 18,
    lineHeight: '1.7em',
    paddingBottom: '5.82%',
    fontFamily: 'OpenSansRegular',
    color: '#666666',
  },
  button: {
    color: '#FFFFFF',
    letterSpacing: 1,
    width: '80%',
    fontSize: 15,
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
    fontFamily: 'MontserratBold',
    fontWeight: '700',
    lineHeight: '1.8em',
    textTransform: 'uppercase',
    backgroundColor: '#E09900',
    marginBottom: '5.82%',
    padding: 10,
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      marginBottom: 30,
    },
  },
  socialContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    '&:first-child': {
      marginRight: 25,
    },
    '& a': {
      textDecoration: 'none',
    },
  },
  linkedInWrapper: {
    backgroundColor: '#007bb6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    width: 32,
    borderRadius: 3,
    marginRight: 8,
    cursor: 'pointer',
  },
  youtubeWrapper: {
    backgroundColor: '#a82400',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    width: 32,
    borderRadius: 3,
    marginRight: 8,
    cursor: 'pointer',
  },
  socialIcon: {
    color: '#FFFFFF',
  },
  followLabel: {
    fontSize: 14,
    lineHeight: '1.6em',
    padding: '0 12px',
    borderRadius: 3,
    transition: 'all .3s',
    '-webkit-transition': 'all .3s',
    fontFamily: 'OpenSansRegular',
    backgroundColor: 'rgba(0,0,0,.6)',
    cursor: 'pointer',
    color: 'hsla(0,0%,100%,.8)',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,.7)',
      color: '#FFFFFF',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: 11,
    },
  },
}));

function ParallaxC({ onPageExit }) {
  const classes = useStyles();

  return (
    <section id="cta">
      <div className={classes.wrapper}>
        <div className={classes.parallaxWrap}>
          <div className={classes.parallaxBg} />
        </div>
        <Grid container spacing={4} className={classes.container}>
          <Grid item xs={12} md={6} className={classes.leftColumn}>
            <LazyAnimation type="translate-bottom-20">
              <Typography className={classes.header}>
                Let&apos;s Start a Conversation
              </Typography>
            </LazyAnimation>
            <Typography className={classes.body}>
              Schedule a time most convenient for you from our calendars. Click
              below on the solution you&apos;re interested in.
            </Typography>
            <ButtonBase
              component="a"
              href="https://app.prosperworks.com/public/meeting-scheduler/CredibleMind/scott/341255:b087e297-b396-4468-8238-f008c9784581"
              target="_blank"
              classes={{ root: classes.button }}
              disableRipple
              onClick={() =>
                onPageExit(
                  'https://app.prosperworks.com/public/meeting-scheduler/CredibleMind/scott/341255:b087e297-b396-4468-8238-f008c9784581',
                  'cta',
                )
              }
            >
              Hospitals | Communities | Coalitions
            </ButtonBase>
            <ButtonBase
              component="a"
              href="https://app.prosperworks.com/public/meeting-scheduler/CredibleMind/kathy/341255:6def1a3d-1ba6-4c54-a90e-d3bb7ca23f89"
              target="_blank"
              classes={{ root: classes.button }}
              disableRipple
              onClick={() =>
                onPageExit(
                  'https://app.prosperworks.com/public/meeting-scheduler/CredibleMind/kathy/341255:6def1a3d-1ba6-4c54-a90e-d3bb7ca23f89',
                  'cta',
                )
              }
            >
              Employers | Consultants
            </ButtonBase>
            <LazyAnimation type="fade-in">
              <ContactForm />
            </LazyAnimation>
          </Grid>
          <Grid item xs={12} md={6} className={classes.rightColumn}>
            <div className={classes.socialContainer}>
              <div className={classes.linkedInWrapper}>
                <a
                  href="https://www.linkedin.com/company/crediblemind/"
                  target="_blank"
                  onClick={() =>
                    onPageExit(
                      'https://www.linkedin.com/company/crediblemind/',
                      'cta',
                    )
                  }
                >
                  <LinkedInIcon className={classes.socialIcon} />
                </a>
              </div>
              <a
                href="https://www.linkedin.com/company/crediblemind/"
                target="_blank"
                onClick={() =>
                  onPageExit(
                    'https://www.linkedin.com/company/crediblemind/',
                    'cta',
                  )
                }
              >
                <Typography className={classes.followLabel}>Follow</Typography>
              </a>
            </div>
            <div className={classes.socialContainer}>
              <div className={classes.youtubeWrapper}>
                <a
                  href="https://www.youtube.com/channel/UCmBk-TKepmMo5LJmRnXZCog"
                  target="_blank"
                  onClick={() =>
                    onPageExit(
                      'https://www.youtube.com/channel/UCmBk-TKepmMo5LJmRnXZCog',
                      'cta',
                    )
                  }
                >
                  <YouTubeIcon className={classes.socialIcon} />
                </a>
              </div>
              <a
                href="https://www.youtube.com/channel/UCmBk-TKepmMo5LJmRnXZCog"
                target="_blank"
                onClick={() =>
                  onPageExit(
                    'https://www.youtube.com/channel/UCmBk-TKepmMo5LJmRnXZCog',
                    'cta',
                  )
                }
              >
                <Typography className={classes.followLabel}>Follow</Typography>
              </a>
            </div>
          </Grid>
        </Grid>
      </div>
    </section>
  );
}

ParallaxC.propTypes = {
  onPageExit: PropTypes.func,
};

export default ParallaxC;
